'use client'

import { ReactNode } from 'react'
import ConsoleShot, { ShotName } from '@/components/console/ConsoleShot'
import FeatureIcon from '@/components/FeatureIcon'
import styles from './ConsoleFeature.module.css'

// One feature of the console, shown as Linear shows theirs: the title and what it is, then
// pictures of the real thing cropped to the feature, then what else it holds. The pictures are
// the console's own (npm run console:shots), so a redesign of the console reaches the page by
// re-running the script. `mirror` puts the large picture on the right, so two in a row alternate.
export default function ConsoleFeature({ id, eyebrow, title, lead, href, linkLabel = 'Learn more', chips, main, second, features, mirror }: {
  id: string
  eyebrow: string
  title: ReactNode
  lead: string
  /** "Learn more" goes here; left out, there is no link (a feature without public docs yet). */
  href?: string
  /** The link's words, when it is not "Learn more". */
  linkLabel?: string
  /** KeywordChips with the `chip` class from this module's styles and a position of their own. */
  chips?: ReactNode
  main: { name: ShotName; alt: string }
  second: { name: ShotName; alt: string }
  /** In IconGrid's look: the console's own icon for each, where it has one. */
  features: { icon: ReactNode; label: string }[]
  mirror?: boolean
}) {
  return (
    <section className={`${styles.wrapper} ${mirror ? styles.mirror : ''}`} aria-labelledby={`${id}-title`}>
      <div className={styles.head}>
        <div>
          <span className={styles.eyebrow}>{eyebrow}</span>
          <h2 id={`${id}-title`}>{title}</h2>
        </div>
        <div className={styles.lead}>
          <p>{lead}</p>
          {href && <a href={href}>{linkLabel} →</a>}
        </div>
      </div>
      <div className={styles.stage}>
        {/* What the pictures show, named over them. */}
        {chips}
        <ConsoleShot name={main.name} className={styles.main} alt={main.alt} />
        <ConsoleShot name={second.name} className={styles.second} sizes="(max-width: 1000px) 90vw, 700px" alt={second.alt} />
      </div>
      <div className={styles.features}>
        <span className={styles.label}>Features</span>
        <div className={styles.list}>{features.map(f => <FeatureIcon key={f.label} icon={f.icon} label={f.label} />)}</div>
      </div>
    </section>
  )
}

export const chipClass = styles.chip
